import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { catalogService, CatalogItem } from '../../services/catalog.service';
import { internalTransferService, Wallet, InternalTransfer } from '../../services/internal-transfer.service';
import {
  internalDepositService,
  Deposit,
  CashClosing,
  LocationBalance,
} from '../../services/internal-deposit.service';
import { LoadingState } from '../../components/ui/LoadingState';

type Tab = 'transfers' | 'deposits' | 'closings';

const fc = (n: number) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  }).format(n || 0);

export function InternalPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState<Tab>('transfers');
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [locations, setLocations] = useState<CatalogItem[]>([]);
  const [currencyTypes, setCurrencyTypes] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canManageClosings = ['sys_admin', 'general_manager', 'accountant'].includes(user?.role ?? '');
  const canApproveClosings = ['sys_admin', 'general_manager'].includes(user?.role ?? '');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [walletsData, locs, currencies] = await Promise.all([
        internalTransferService.getMyWallets(),
        catalogService.list('financial_locations'),
        catalogService.list('currency_types'),
      ]);
      setWallets(walletsData);
      setLocations(locs);
      setCurrencyTypes(currencies);
    } catch (e: any) {
      setError(e?.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingState message="Cargando..." />;
  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
      {error}
    </div>
  );

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Interno</h1>
        <p className="text-xs text-slate-500">Transferencias, depósitos y cierres</p>
      </div>

      <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm">
        <button
          onClick={() => setActiveTab('transfers')}
          className={`flex-1 py-2 rounded-md text-[11px] font-semibold ${
            activeTab === 'transfers' ? 'bg-primary text-white' : 'text-slate-600'
          }`}
        >
          📤 Transf.
        </button>
        <button
          onClick={() => setActiveTab('deposits')}
          className={`flex-1 py-2 rounded-md text-[11px] font-semibold ${
            activeTab === 'deposits' ? 'bg-primary text-white' : 'text-slate-600'
          }`}
        >
          💰 Depósitos
        </button>
        {canManageClosings && (
          <button
            onClick={() => setActiveTab('closings')}
            className={`flex-1 py-2 rounded-md text-[11px] font-semibold ${
              activeTab === 'closings' ? 'bg-primary text-white' : 'text-slate-600'
            }`}
          >
            📊 Cierres
          </button>
        )}
      </div>

      {activeTab === 'transfers' && (
        <TransfersTab
          wallets={wallets}
          onReload={load}
          showSuccess={showSuccess}
          showError={showError}
          confirm={confirm}
        />
      )}

      {activeTab === 'deposits' && (
        <DepositsTab
          wallets={wallets}
          locations={locations}
          onReload={load}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}

      {activeTab === 'closings' && canManageClosings && (
        <ClosingsTab
          canApprove={canApproveClosings}
          locations={locations}
          currencyTypes={currencyTypes}
          showSuccess={showSuccess}
          showError={showError}
          confirm={confirm}
        />
      )}
    </div>
  );
}

// ============================================================
// TRANSFERS TAB
// ============================================================
function TransfersTab({
  wallets,
  onReload,
  showSuccess,
  showError,
  confirm,
}: {
  wallets: Wallet[];
  onReload: () => Promise<void>;
  showSuccess: (m: string) => void;
  showError: (m: string) => void;
  confirm: (opts: any) => Promise<boolean>;
}) {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState<InternalTransfer[]>([]);
  const [fromWalletId, setFromWalletId] = useState('');
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [activeTransfer, setActiveTransfer] = useState<(InternalTransfer & { qr_url: string }) | null>(null);
  const [qrTokenInput, setQrTokenInput] = useState('');
  const [isReceiving, setIsReceiving] = useState(false);

  const loadTransfers = async () => {
    try {
      const data = await internalTransferService.list();
      setTransfers(data);
    } catch (e: any) {
      showError(e?.message || 'Error');
    }
  };

  useEffect(() => {
    loadTransfers();
  }, []);

  useEffect(() => {
    if (!fromWalletId && wallets.length > 0) {
      const withBalance = wallets.find((w) => w.balance > 0);
      setFromWalletId(withBalance?.id || wallets[0].id);
    }
  }, [wallets]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    if (!fromWalletId || amount <= 0) {
      showError('Selecciona wallet y monto válido');
      setIsCreating(false);
      return;
    }
    const wallet = wallets.find((w) => w.id === fromWalletId);
    if (!wallet || wallet.balance < amount) {
      showError('Saldo insuficiente');
      setIsCreating(false);
      return;
    }
    try {
      const transfer = await internalTransferService.create({
        from_wallet_id: fromWalletId,
        currency_type_id: wallet.currency_type_id,
        amount,
        notes: notes || undefined,
      });
      setActiveTransfer(transfer);
      setAmount(0);
      setNotes('');
      await loadTransfers();
    } catch (e: any) {
      showError(e?.message || 'Error al crear');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = async (id: string) => {
    if (!(await confirm({ message: '¿Cancelar transferencia?', danger: true }))) return;
    try {
      await internalTransferService.cancel(id);
      setActiveTransfer(null);
      await loadTransfers();
      showSuccess('Cancelada');
    } catch (e: any) {
      showError(e?.message || 'Error');
    }
  };

  const handleReceive = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsReceiving(true);
    try {
      const result = await internalTransferService.receive(qrTokenInput.trim());
      showSuccess(`Recibida. Nuevo saldo: ${fc(result.new_balance)}`);
      setQrTokenInput('');
      await loadTransfers();
      await onReload();
    } catch (e: any) {
      showError(e?.message || 'Error');
    } finally {
      setIsReceiving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl shadow-sm p-3">
        <p className="text-xs font-bold mb-2">💼 Mis Wallets</p>
        <div className="grid grid-cols-2 gap-2">
          {wallets.map((wallet) => (
            <button
              key={wallet.id}
              onClick={() => setFromWalletId(wallet.id)}
              className={`p-2 rounded-lg text-left border ${
                wallet.id === fromWalletId
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200'
              }`}
            >
              <p className="text-[10px] text-slate-500">{wallet.currency_name}</p>
              <p className="text-sm font-bold text-primary">{fc(wallet.balance)}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-bold mb-2">📤 Enviar</h3>
        <form onSubmit={handleCreate} className="space-y-2">
          <select
            required
            value={fromWalletId}
            onChange={(e) => setFromWalletId(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">Seleccionar wallet...</option>
            {wallets.map((w) => (
              <option key={w.id} value={w.id}>
                {w.currency_name} — {fc(w.balance)}
              </option>
            ))}
          </select>
          <input
            type="number"
            required
            min="0.01"
            step="0.01"
            value={amount || ''}
            onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
            placeholder="Monto"
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
          />
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notas (opcional)"
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
          />
          <button
            type="submit"
            disabled={isCreating}
            className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
          >
            {isCreating ? 'Generando...' : '📱 Generar QR'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-bold mb-2">📥 Recibir</h3>
        <form onSubmit={handleReceive} className="space-y-2">
          <input
            type="text"
            required
            value={qrTokenInput}
            onChange={(e) => setQrTokenInput(e.target.value)}
            placeholder="Token del QR"
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
          />
          <button
            type="submit"
            disabled={isReceiving}
            className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
          >
            {isReceiving ? 'Recibiendo...' : '✅ Recibir'}
          </button>
        </form>
      </div>

      {activeTransfer && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="text-sm font-bold mb-2">📱 QR Generado</h3>
          <div className="flex flex-col items-center">
            <div className="p-3 bg-white border border-slate-200 rounded-lg inline-block">
              <QRCodeSVG value={activeTransfer.qr_url} size={200} level="M" />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {fc(activeTransfer.amount)} — Expira {new Date(activeTransfer.qr_expires_at).toLocaleString('es-MX')}
            </p>
            <div className="flex gap-2 mt-2 w-full">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(activeTransfer.qr_token);
                  showSuccess('Token copiado');
                }}
                className="flex-1 py-2 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold"
              >
                📋 Copiar
              </button>
              <button
                onClick={() => handleCancel(activeTransfer.id)}
                className="flex-1 py-2 rounded-lg bg-red-50 text-red-600 text-xs font-semibold"
              >
                ❌ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-bold mb-2">📜 Historial</h3>
        {transfers.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-3">Sin transferencias</p>
        ) : (
          <ul className="space-y-2">
            {transfers.slice(0, 15).map((t) => {
              const isOut = t.created_by === user?.id;
              const badges: Record<string, string> = {
                pending: 'bg-amber-100 text-amber-700',
                completed: 'bg-emerald-100 text-emerald-700',
                cancelled: 'bg-slate-100 text-slate-500',
                expired: 'bg-slate-100 text-slate-500',
              };
              return (
                <li key={t.id} className="border-b border-slate-100 pb-1.5 last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate">
                        {isOut ? `→ ${t.to_user_name || 'Pendiente'}` : `← ${t.from_user_name || 'Desconocido'}`}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {new Date(t.created_at).toLocaleString('es-MX', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold">{fc(t.amount)}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${badges[t.status] || 'bg-slate-100'}`}>
                        {t.status}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
// DEPOSITS TAB
// ============================================================
function DepositsTab({
  wallets,
  locations,
  onReload,
  showSuccess,
  showError,
}: {
  wallets: Wallet[];
  locations: CatalogItem[];
  onReload: () => Promise<void>;
  showSuccess: (m: string) => void;
  showError: (m: string) => void;
}) {
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [fromWalletId, setFromWalletId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadDeposits();
  }, []);

  useEffect(() => {
    if (!fromWalletId && wallets.length > 0) {
      const withBalance = wallets.find((w) => w.balance > 0);
      setFromWalletId(withBalance?.id || wallets[0].id);
    }
  }, [wallets]);

  const loadDeposits = async () => {
    try {
      const data = await internalDepositService.listMyDeposits();
      setDeposits(data);
    } catch (e: any) {
      showError(e?.message || 'Error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    if (!fromWalletId || !toLocationId || amount <= 0) {
      showError('Completa todos los campos');
      setIsCreating(false);
      return;
    }
    const wallet = wallets.find((w) => w.id === fromWalletId);
    if (!wallet || wallet.balance < amount) {
      showError(`Saldo insuficiente. Disponible: ${fc(wallet?.balance || 0)}`);
      setIsCreating(false);
      return;
    }
    try {
      await internalDepositService.createDeposit({
        from_wallet_id: fromWalletId,
        to_location_id: toLocationId,
        currency_type_id: wallet.currency_type_id,
        amount,
        notes: notes || undefined,
      });
      showSuccess('Depósito registrado');
      setAmount(0);
      setNotes('');
      await loadDeposits();
      await onReload();
    } catch (e: any) {
      showError(e?.message || 'Error');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-bold mb-2">💰 Registrar Depósito</h3>
        <form onSubmit={handleSubmit} className="space-y-2">
          <select
            required
            value={fromWalletId}
            onChange={(e) => setFromWalletId(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">Wallet origen...</option>
            {wallets.map((w) => (
              <option key={w.id} value={w.id}>
                {w.currency_name} — {fc(w.balance)}
              </option>
            ))}
          </select>
          <select
            required
            value={toLocationId}
            onChange={(e) => setToLocationId(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">Ubicación destino...</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          <input
            type="number"
            required
            min="0.01"
            step="0.01"
            value={amount || ''}
            onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
            placeholder="Monto"
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
          />
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notas (opcional)"
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
          />
          <button
            type="submit"
            disabled={isCreating}
            className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
          >
            {isCreating ? 'Registrando...' : '💰 Depositar'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-bold mb-2">📜 Mis Depósitos</h3>
        {deposits.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-3">Sin depósitos registrados</p>
        ) : (
          <ul className="space-y-2">
            {deposits.slice(0, 15).map((d) => (
              <li key={d.id} className="border-b border-slate-100 pb-1.5 last:border-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{d.location_name || '—'}</p>
                    <p className="text-[10px] text-slate-400">
                      {new Date(d.deposit_date).toLocaleString('es-MX')}
                    </p>
                  </div>
                  <p className="text-sm font-bold flex-shrink-0">{fc(d.amount)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
// CLOSINGS TAB
// ============================================================
function ClosingsTab({
  canApprove,
  locations,
  currencyTypes,
  showSuccess,
  showError,
  confirm,
}: {
  canApprove: boolean;
  locations: CatalogItem[];
  currencyTypes: CatalogItem[];
  showSuccess: (m: string) => void;
  showError: (m: string) => void;
  confirm: (opts: any) => Promise<boolean>;
}) {
  const [balances, setBalances] = useState<LocationBalance[]>([]);
  const [closings, setClosings] = useState<CashClosing[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [currencyTypeId, setCurrencyTypeId] = useState('');
  const [closingDate, setClosingDate] = useState(new Date().toISOString().slice(0, 10));
  const [physicalCount, setPhysicalCount] = useState(0);
  const [justification, setJustification] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [bals, cls] = await Promise.all([
        internalDepositService.getLocationBalances(),
        internalDepositService.listClosings(),
      ]);
      setBalances(bals);
      setClosings(cls);
    } catch (e: any) {
      showError(e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const getSelectedBalance = () => {
    const b = balances.find(
      (b) => b.location_id === locationId && b.currency_type_id === currencyTypeId
    );
    return b?.balance ?? 0;
  };

  const difference = physicalCount - getSelectedBalance();

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    if (!locationId || !currencyTypeId) {
      showError('Selecciona ubicación y moneda');
      setIsSaving(false);
      return;
    }
    if (Math.abs(difference) > 0.01 && !justification.trim()) {
      showError(`Diferencia de ${fc(difference)}. Debes justificarla.`);
      setIsSaving(false);
      return;
    }
    try {
      await internalDepositService.createClosing({
        location_id: locationId,
        currency_type_id: currencyTypeId,
        closing_date: closingDate,
        physical_count: physicalCount,
        justification: justification || undefined,
      });
      showSuccess('Cierre registrado');
      setShowForm(false);
      setLocationId('');
      setCurrencyTypeId('');
      setPhysicalCount(0);
      setJustification('');
      await load();
    } catch (e: any) {
      showError(e?.message || 'Error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdjust = async (id: string) => {
    if (!(await confirm({ message: '¿Ajustar saldo? Se creará un movimiento de ajuste.', danger: true }))) return;
    try {
      await internalDepositService.applyAdjustment(id);
      showSuccess('Ajuste aplicado');
      await load();
    } catch (e: any) {
      showError(e?.message || 'Error');
    }
  };

  const handleApprove = async (id: string) => {
    if (!(await confirm({ message: '¿Aprobar cierre? No se podrá modificar.', danger: true }))) return;
    try {
      await internalDepositService.approveClosing(id);
      showSuccess('Cierre aprobado');
      await load();
    } catch (e: any) {
      showError(e?.message || 'Error');
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold">💼 Saldos por Ubicación</h3>
          <button
            onClick={() => setShowForm(!showForm)}
            className="text-xs text-primary font-semibold"
          >
            {showForm ? 'Cancelar' : '+ Nuevo Cierre'}
          </button>
        </div>
        {balances.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">Sin saldos registrados</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {balances.map((b, i) => (
              <div key={i} className="bg-slate-50 rounded-lg p-2">
                <p className="text-[10px] text-slate-500 truncate">{b.location_name}</p>
                <p className="text-[10px] text-slate-400">{b.currency_name || '—'}</p>
                <p className="text-sm font-bold text-primary">{fc(b.balance ?? 0)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-primary">
          <h3 className="text-sm font-bold mb-2">📊 Nuevo Cierre</h3>
          <form onSubmit={handleCreate} className="space-y-2">
            <select
              required
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            >
              <option value="">Ubicación...</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            <select
              required
              value={currencyTypeId}
              onChange={(e) => setCurrencyTypeId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            >
              <option value="">Moneda...</option>
              {currencyTypes.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {locationId && currencyTypeId && (
              <p className="text-xs bg-slate-50 px-3 py-2 rounded-lg">
                Saldo sistema: <strong>{fc(getSelectedBalance())}</strong>
              </p>
            )}
            <input
              type="date"
              required
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={physicalCount || ''}
              onChange={(e) => setPhysicalCount(parseFloat(e.target.value) || 0)}
              placeholder="Conteo físico"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
            {Math.abs(difference) > 0.01 && (
              <div className={`text-xs p-2 rounded-lg ${difference < 0 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                Diferencia: <strong>{fc(difference)}</strong> ({difference < 0 ? 'Faltante' : 'Sobrante'})
              </div>
            )}
            <textarea
              rows={2}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder={Math.abs(difference) > 0.01 ? 'Justificación (obligatoria)' : 'Justificación (opcional)'}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
            />
            <button
              type="submit"
              disabled={isSaving}
              className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
            >
              {isSaving ? 'Guardando...' : 'Registrar Cierre'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-bold mb-2">📜 Historial de Cierres</h3>
        {closings.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-3">Sin cierres registrados</p>
        ) : (
          <ul className="space-y-2">
            {closings.slice(0, 15).map((c) => (
              <li key={c.id} className="border-b border-slate-100 pb-1.5 last:border-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-mono text-slate-500">{c.closing_number}</p>
                    <p className="text-xs font-medium truncate">{c.location_name}</p>
                    <p className="text-[10px] text-slate-400">
                      {new Date(c.closing_date).toLocaleDateString('es-MX')}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-xs font-bold ${Math.abs(c.difference) < 0.01 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {fc(c.difference)}
                    </p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      c.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      c.status === 'closed' ? 'bg-primary/10 text-primary' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {c.status === 'approved' ? '✅ Aprobado' : c.status === 'closed' ? '🔒 Cerrado' : '⏳ Abierto'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5 mt-1">
                  {c.status !== 'approved' && Math.abs(c.difference) > 0.01 && (
                    <button
                      onClick={() => handleAdjust(c.id)}
                      className="flex-1 py-1 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold"
                    >
                      ⚖️ Ajustar
                    </button>
                  )}
                  {c.status !== 'approved' && canApprove && (
                    <button
                      onClick={() => handleApprove(c.id)}
                      className="flex-1 py-1 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold"
                    >
                      ✅ Aprobar
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}